'use client';

import * as React from 'react';
import { Tooltip, TooltipTrigger, TooltipContent } from './tooltip';

interface ActionBarTooltipProps {
  label: string;
  children: React.ReactNode;
}

/**
 * Simple tooltip wrapper for action bar buttons.
 * Shows tooltip below the trigger element.
 */
export function ActionBarTooltip({ label, children }: ActionBarTooltipProps) {
  if (!React.isValidElement(children)) {
    throw new Error('ActionBarTooltip expects a single React element child.');
  }

  // Only add aria-label for accessibility; do NOT set title attribute
  // as it causes duplicate tooltips (custom tooltip + native browser tooltip)
  const childProps = children.props as Record<string, unknown>;
  const needsAriaLabel = childProps['aria-label'] == null && childProps['aria-labelledby'] == null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const labelledChild = needsAriaLabel ? React.cloneElement(children as any, { 'aria-label': label }) : children;
  const labelledElement = labelledChild as React.ReactElement;
  const isDisabledButton =
    typeof labelledElement.type === 'string' &&
    labelledElement.type === 'button' &&
    Boolean((labelledElement.props as { disabled?: unknown }).disabled);
  const triggerChild = isDisabledButton ? <span className='inline-flex'>{labelledChild}</span> : labelledChild;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{triggerChild}</TooltipTrigger>
      <TooltipContent side='bottom' sideOffset={4}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}
